import { Drawer, type DrawerProps } from "@mui/material";
import { useMemo } from "react";

type PersistentDrawerProps = {
  open?: boolean;
  children: React.ReactNode;
  shouldNotTrackHeight?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  borderRadius?: string;
};

export function PersistentDrawer({
  children,
  open = true,
  shouldNotTrackHeight = false,
  noShadow = false,
  noBorder = false,
  borderRadius = "var(--radius-4xl)",
}: PersistentDrawerProps) {
  const slotProps = useMemo<DrawerProps["slotProps"]>(() => {
    return {
      paper: {
        style: {
          height: "auto",
          borderRadius,
          background: "#fff",
          border: noBorder ? "none" : undefined,
          boxShadow: noShadow
            ? "none"
            : "0 -1px 12px 0 rgba(0, 0, 0, 0.08), 0 -4px 12px 0 rgba(0, 0, 0, 0.04)",
        },
        ref: (node: HTMLDivElement) => {
          if (shouldNotTrackHeight) return;

          const observer = new ResizeObserver(([entry]) => {
            document.body.style.setProperty(
              "--drawer-height",
              `${entry.target.getBoundingClientRect().height}px`,
            );
          });

          observer.observe(node);

          return () => {
            observer.disconnect();
            document.body.style.setProperty("--drawer-height", "0");
          };
        },
      },
    };
  }, [borderRadius, noBorder, noShadow, shouldNotTrackHeight]);

  return (
    <Drawer
      slotProps={slotProps}
      anchor="bottom"
      variant="persistent"
      open={open}
    >
      <div className="w-full">{children}</div>
    </Drawer>
  );
}
